import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import {legalVaultService} from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSave, FiBriefcase, FiGlobe, FiMail, FiPhone, FiMapPin, FiAward, FiPlus, FiImage, FiX, FiCheckCircle, FiXCircle, FiEdit2, FiAlertCircle} = FiIcons;

// Alert Modal Component
function AlertModal({message, onClose}) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-2 text-blue-600" /> Alert
          </h3>
          <p className="text-slate-600 mt-2">{message}</p>
        </div>
        <div className="flex justify-end p-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [activeTab, setActiveTab] = useState('global');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState(null);
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [removeLogoRequested, setRemoveLogoRequested] = useState(false);
  const [removeBrandLogoRequested, setRemoveBrandLogoRequested] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // A. Settings Data Loader
  const loadSettingsData = async () => {
    const [globalSettings, defaultBrand, profiles] = await Promise.all([
      legalVaultService.getGlobalSettings(),
      legalVaultService.getDefaultBrandingProfile(),
      legalVaultService.getBrandingProfiles(),
    ]);
    const mergedSettings = {
      ...globalSettings,
      businessName: globalSettings.businessName || defaultBrand?.name || '',
      tradingName: globalSettings.tradingName || defaultBrand?.tradingName || '',
      abn: globalSettings.abn || defaultBrand?.abn || '',
      acn: globalSettings.acn || defaultBrand?.acn || '',
      websiteUrl: globalSettings.websiteUrl || defaultBrand?.website || '',
      phone: globalSettings.phone || defaultBrand?.phone || '',
      supportEmail: globalSettings.supportEmail || defaultBrand?.supportEmail || '',
      legalEmail: globalSettings.legalEmail || defaultBrand?.legalEmail || '',
      address: globalSettings.address || defaultBrand?.address || '',
      footerDisclaimer: globalSettings.footerDisclaimer || defaultBrand?.footerDisclaimer || '',
    };
    setSettings(mergedSettings);
    setBrands(profiles);
    setLogoPreview(defaultBrand?.logo || null);
  };

  // A. Use loadSettingsData in useEffect
  useEffect(() => {
    loadSettingsData().catch((error) => {
      console.error('Unable to load settings data:', error);
      setErrorMessage(`Unable to load settings: ${error.message}`);
    });
  }, []);

  // Clear error message after timeout
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // B. Use ensureDefaultBrandingProfile during Global Settings Save
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setErrorMessage(null);

      // Prepare text-based global settings updates (excluding logo)
      const textSettings = {
        businessName: settings.businessName,
        tradingName: settings.tradingName,
        abn: settings.abn,
        acn: settings.acn,
        websiteUrl: settings.websiteUrl,
        phone: settings.phone,
        supportEmail: settings.supportEmail,
        legalEmail: settings.legalEmail,
        address: settings.address,
        footerDisclaimer: settings.footerDisclaimer,
        exportFooterText: settings.exportFooterText,
      };

      // Save global settings first
      await legalVaultService.updateGlobalSettings(textSettings);

      // B. Use ensureDefaultBrandingProfile instead of brands.find
      const brand = await legalVaultService.ensureDefaultBrandingProfile(textSettings);

      // Build branding updates
      const brandingUpdates = {
        name: settings.businessName || settings.tradingName || 'BPA Solutions',
        tradingName: settings.tradingName || '',
        abn: settings.abn || '',
        acn: settings.acn || '',
        website: settings.websiteUrl || '',
        phone: settings.phone || '',
        legalEmail: settings.legalEmail || '',
        supportEmail: settings.supportEmail || '',
        address: settings.address || '',
        footerDisclaimer: settings.footerDisclaimer || '',
        updatedAt: new Date().toISOString(),
      };

      // Add logo file only if it's a new File
      if (logoFile instanceof File) {
        brandingUpdates.logo = logoFile;
      }
      // Add removeLogo flag only if requested
      if (removeLogoRequested) {
        brandingUpdates.removeLogo = true;
      }

      // Update branding profile
      await legalVaultService.updateBrandingProfile(brand.id, brandingUpdates);

      // Reload data after save
      await loadSettingsData();

      // Reset file states
      setLogoFile(null);
      setRemoveLogoRequested(false);

      // Show success
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save settings error:', error);
      setErrorMessage('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('File size must be less than 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    setLogoFile(file);
    setRemoveLogoRequested(false);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    setRemoveLogoRequested(true);
  };

  // C. Fix Branding Profile Logo Editing
  const handleEditBrand = (brand) => {
    setEditingBrand({...brand});
    setBrandLogoPreview(brand.logo);
    setBrandLogoFile(null);
    setRemoveBrandLogoRequested(false);
  };

  const handleSaveBrand = async () => {
    try {
      if (!editingBrand) return;
      const updates = {
        name: editingBrand.name,
        tradingName: editingBrand.tradingName,
        website: editingBrand.website,
        abn: editingBrand.abn,
        acn: editingBrand.acn,
        phone: editingBrand.phone,
        email: editingBrand.email,
        legalEmail: editingBrand.legalEmail,
        supportEmail: editingBrand.supportEmail,
        address: editingBrand.address,
        footerDisclaimer: editingBrand.footerDisclaimer,
      };

      // Add logo file only if it's a new File
      if (brandLogoFile instanceof File) {
        updates.logo = brandLogoFile;
      }
      // Add removeLogo flag only if requested
      if (removeBrandLogoRequested) {
        updates.removeLogo = true;
      }

      await legalVaultService.updateBrandingProfile(editingBrand.id, updates);

      // Reload data after save
      await loadSettingsData();

      // Reset states
      setEditingBrand(null);
      setBrandLogoPreview(null);
      setBrandLogoFile(null);
      setRemoveBrandLogoRequested(false);

      // D. Use success state instead of error message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save brand error:', error);
      setErrorMessage('Failed to update branding profile: ' + error.message);
    }
  };

  const handleBrandLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('File size must be less than 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setBrandLogoPreview(e.target.result);
      setBrandLogoFile(file);
    };
    reader.readAsDataURL(file);
    setRemoveBrandLogoRequested(false);
  };

  const handleRemoveBrandLogo = () => {
    setBrandLogoPreview(null);
    setBrandLogoFile(null);
    setEditingBrand({...editingBrand, logo: null});
    setRemoveBrandLogoRequested(true);
  };

  const handleCancelBrandEdit = () => {
    setEditingBrand(null);
    setBrandLogoPreview(null);
    setBrandLogoFile(null);
    setRemoveBrandLogoRequested(false);
  };

  if (!settings) return null;

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="space-y-6 max-w-5xl"
    >
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Application Settings</h2>
        <p className="text-slate-500 mt-1">Configure global platform defaults and branding profiles.</p>
      </div>

      {/* System Status Notice */}
      <div className={`rounded-lg p-4 flex items-start ${legalVaultService.isConnected() ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <SafeIcon icon={legalVaultService.isConnected() ? FiCheckCircle : FiXCircle} className={`${legalVaultService.isConnected() ? 'text-green-600' : 'text-red-600'} mr-2 mt-1`} />
        <div>
          <span className={`${legalVaultService.isConnected() ? 'text-green-700' : 'text-red-700'} font-medium`}>
            {legalVaultService.isConnected() ? 'System Live. Settings are stored securely.' : 'Secure backend unavailable. Settings cannot be saved.'}
          </span>
        </div>
      </div>

      {/* Success and Error Messages */}
      {saveSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center">
          <SafeIcon icon={FiCheckCircle} className="text-emerald-600 mr-2" />
          <span className="text-emerald-700 font-medium">Settings saved successfully!</span>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <SafeIcon icon={FiAlertCircle} className="text-red-600 mr-2 mt-1" />
          <div>
            <span className="text-red-700 font-medium">Error: {errorMessage}</span>
          </div>
        </div>
      )}

      <div className="flex space-x-1 border-b border-slate-200">
        <button
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'global' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
          onClick={() => setActiveTab('global')}
        >
          <SafeIcon icon={FiGlobe} className="inline mr-2" /> Global Defaults
        </button>
        <button
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'brands' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
          onClick={() => setActiveTab('brands')}
        >
          <SafeIcon icon={FiAward} className="inline mr-2" /> Branding Profiles
        </button>
      </div>

      {activeTab === 'global' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-8">
          {/* Logo Upload Section */}
          <section>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <SafeIcon icon={FiImage} className="mr-2 text-slate-400" /> Company Logo
            </h3>
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 bg-slate-50">
              {logoPreview ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <img src={logoPreview} alt="Company Logo" className="w-20 h-20 object-contain rounded-lg border border-slate-200 bg-white p-2" />
                    <div>
                      <p className="text-sm text-slate-600">Current logo</p>
                      <p className="text-xs text-slate-500">Click to replace or remove</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <label className="cursor-pointer px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                      <SafeIcon icon={FiImage} className="mr-1" /> Replace
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                    <button onClick={handleRemoveLogo} className="px-3 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors">
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-200 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiImage} className="text-slate-400 text-2xl" />
                  </div>
                  <label className="cursor-pointer px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors inline-block">
                    Upload Logo
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                  <p className="text-xs text-slate-500 mt-2">PNG, JPG, GIF up to 5MB</p>
                </div>
              )}
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <SafeIcon icon={FiBriefcase} className="mr-2 text-slate-400" /> Business Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={settings.businessName || ''}
                  onChange={(e) => setSettings({...settings, businessName: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Trading Name</label>
                <input
                  type="text"
                  value={settings.tradingName || ''}
                  onChange={(e) => setSettings({...settings, tradingName: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">ABN</label>
                <input
                  type="text"
                  value={settings.abn || ''}
                  onChange={(e) => setSettings({...settings, abn: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">ACN</label>
                <input
                  type="text"
                  value={settings.acn || ''}
                  onChange={(e) => setSettings({...settings, acn: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <SafeIcon icon={FiMail} className="mr-2 text-slate-400" /> Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label>
                <input
                  type="url"
                  value={settings.websiteUrl || ''}
                  onChange={(e) => setSettings({...settings, websiteUrl: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={settings.phone || ''}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Support Email</label>
                <input
                  type="email"
                  value={settings.supportEmail || ''}
                  onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Legal Email</label>
                <input
                  type="email"
                  value={settings.legalEmail || ''}
                  onChange={(e) => setSettings({...settings, legalEmail: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Address</label>
                <textarea
                  value={settings.address || ''}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  rows={2}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                ></textarea>
              </div>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Document Defaults</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Default Footer Disclaimer</label>
                <input
                  type="text"
                  value={settings.footerDisclaimer || ''}
                  onChange={(e) => setSettings({...settings, footerDisclaimer: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Default Export Footer Text</label>
                <input
                  type="text"
                  value={settings.exportFooterText || ''}
                  onChange={(e) => setSettings({...settings, exportFooterText: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end pt-4">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'brands' && (
        <div className="space-y-4">
          {/* C. Hide dead Add Profile button */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {brands.map(brand => (
              <div key={brand.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col">
                {/* View Mode */}
                {!editingBrand || editingBrand.id !== brand.id ? (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
                          {brand.logo ? (
                            <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-slate-400 font-bold text-lg">{brand.name.substring(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="ml-4">
                          <h4 className="font-semibold text-slate-900 text-lg flex items-center">
                            {brand.name}
                            {brand.isDefault && (
                              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] uppercase font-bold rounded">Default</span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-500">{brand.website}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-auto pt-4 border-t border-slate-100 flex justify-end space-x-3">
                      {brand.isDefault && (
                        <button
                          onClick={() => handleEditBrand(brand)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <SafeIcon icon={FiEdit2} className="mr-1" /> Edit
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  /* Edit Mode */
                  <div className="space-y-4">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Logo</label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50">
                        {brandLogoPreview ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <img src={brandLogoPreview} alt="Brand Logo" className="w-16 h-16 object-contain rounded border border-slate-200 bg-white p-2" />
                              <div>
                                <p className="text-sm text-slate-600">Current logo</p>
                                <p className="text-xs text-slate-500">Click to replace or remove</p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <label className="cursor-pointer px-2.5 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors">
                                <SafeIcon icon={FiImage} className="mr-1" /> Replace
                                <input type="file" accept="image/*" onChange={handleBrandLogoUpload} className="hidden" />
                              </label>
                              <button onClick={handleRemoveBrandLogo} className="px-2.5 py-1.5 border border-red-300 text-red-600 rounded text-sm font-medium hover:bg-red-50 transition-colors">
                                <SafeIcon icon={FiX} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="w-12 h-12 mx-auto mb-3 bg-slate-200 rounded flex items-center justify-center">
                              <SafeIcon icon={FiImage} className="text-slate-400 text-xl" />
                            </div>
                            <label className="cursor-pointer px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors inline-block">
                              Upload Logo
                              <input type="file" accept="image/*" onChange={handleBrandLogoUpload} className="hidden" />
                            </label>
                            <p className="text-xs text-slate-500 mt-2">PNG, JPG, GIF up to 5MB</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Business Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                        <input
                          type="text"
                          value={editingBrand.name || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, name: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Trading Name</label>
                        <input
                          type="text"
                          value={editingBrand.tradingName || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, tradingName: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">ABN</label>
                        <input
                          type="text"
                          value={editingBrand.abn || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, abn: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">ACN</label>
                        <input
                          type="text"
                          value={editingBrand.acn || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, acn: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                        <input
                          type="text"
                          value={editingBrand.website || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, website: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                        <input
                          type="text"
                          value={editingBrand.phone || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, phone: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={editingBrand.email || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, email: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Legal Email</label>
                        <input
                          type="email"
                          value={editingBrand.legalEmail || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, legalEmail: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                        <textarea
                          value={editingBrand.address || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, address: e.target.value})}
                          rows={2}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        ></textarea>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Footer Disclaimer</label>
                        <input
                          type="text"
                          value={editingBrand.footerDisclaimer || ''}
                          onChange={(e) => setEditingBrand({...editingBrand, footerDisclaimer: e.target.value})}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
                      <button
                        onClick={handleCancelBrandEdit}
                        className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveBrand}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        <SafeIcon icon={FiSave} className="mr-1" /> Save Profile
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal message={errorMessage} onClose={() => setErrorMessage(null)} />
    </motion.div>
  );
}